import { useRef, useState } from "react";
import { Mic, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { enqueueVoice } from "../../lib/voiceQueue";

// One-click toggle mic: tap to start, tap again to finish & transcribe.
export const VoiceCapture = ({ apiBase, token, active, onResult, onNeedAccess }) => {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  const cleanupStream = () => {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    streamRef.current = null;
  };

  const finish = async () => {
    cleanupStream();
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    chunksRef.current = [];
    if (!blob.size) { setProcessing(false); return; }

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      try { await enqueueVoice(blob); toast("Saved offline — we'll sync it when you're back online."); }
      catch { toast.error("Couldn't save your note."); }
      setProcessing(false);
      return;
    }
    try {
      const fd = new FormData();
      fd.append("audio", blob, "note.webm");
      const r = await fetch(`${apiBase}/voice-note`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (r.status === 401 || r.status === 402) { onNeedAccess?.(); setProcessing(false); return; }
      const d = await r.json();
      if (r.ok) onResult?.(d);
      else toast.error(d.detail || "Couldn't understand that note — try again.");
    } catch {
      try { await enqueueVoice(blob); toast("Saved offline — we'll sync it when you're back online."); }
      catch { toast.error("Couldn't process your note."); }
    }
    setProcessing(false);
  };

  const startRecording = async () => {
    if (!active) { onNeedAccess?.(); return; }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("Voice isn't supported on this device."); return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (ev) => { if (ev.data && ev.data.size) chunksRef.current.push(ev.data); };
      mr.onstop = () => { setProcessing(true); finish(); };
      recorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      cleanupStream();
      toast.error("Please allow microphone access to add by voice.");
    }
  };

  const stopRecording = () => {
    setRecording(false);
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
      else { setProcessing(false); }
    } catch { cleanupStream(); setProcessing(false); }
  };

  const toggle = () => {
    if (processing) return;
    if (recording) stopRecording();
    else startRecording();
  };

  const copy = processing ? "Transcribing your note…"
    : recording ? "🎙️ Recording... Tap again to finish"
    : "Tap to add by voice";

  return (
    <div className="flex flex-col items-center gap-3 select-none py-2" data-testid="sns-voice">
      <button
        type="button"
        data-testid="sns-mic-btn"
        onClick={toggle}
        disabled={processing}
        aria-label={recording ? "Tap to finish recording" : "Tap to record a voice note"}
        className={`relative h-24 w-24 rounded-full grid place-items-center text-white shadow-[0_10px_30px_rgba(46,125,79,0.35)] transition-transform duration-150 ${
          recording ? "bg-[#E4572E] scale-110" : "bg-[#2E7D4F] active:scale-95"
        } disabled:opacity-60`}
      >
        {recording && (
          <>
            <span className="absolute inset-0 rounded-full bg-[#E4572E] animate-ping opacity-50" />
            <span className="absolute -inset-2 rounded-full border-2 border-[#E4572E]/40 animate-pulse" />
          </>
        )}
        {processing
          ? <Loader2 className="h-8 w-8 animate-spin relative" />
          : <Mic className="h-9 w-9 relative" />}
      </button>
      <p
        data-testid="sns-mic-copy"
        className={`text-sm font-semibold text-center h-5 transition-colors ${recording ? "text-[#E4572E]" : "text-black/55"}`}
      >
        {copy}
      </p>
    </div>
  );
};
